import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, timer} from 'rxjs';

export interface SubSectionFieldCount {
  subSectionId: string;
  createdCount: number;
  isComplete: boolean;
  lastUpdateTime: number;
  completionTime?: number; // Nouveau: quand la section a été marquée comme complète
}

export interface TypeFieldCountCache {
  type: string;
  expectedCount: number;
  timestamp: number; // Quand cette info a été sauvegardée
}

@Injectable({
  providedIn: 'root'
})
export class EformsFieldCounterService {
  private subSectionCounts = new Map<string, SubSectionFieldCount>();
  private subSectionCountsSubject = new BehaviorSubject<Map<string, SubSectionFieldCount>>(new Map());
  private typeCountCache = new Map<string, TypeFieldCountCache>(); // Cache des compteurs par type
  private completionTimers = new Map<string, any>(); // Timers pour détecter la fin de chargement
  private readonly COMPLETION_DELAY = 500; // 500ms sans activité = fin de chargement

  constructor() {
    this.loadCachedTypeCounts();
  }

  /**
   * Charge les compteurs mis en cache depuis le localStorage
   */
  private loadCachedTypeCounts(): void {
    try {
      const cached = localStorage.getItem('eforms-field-count-cache');
      if (cached) {
        const data = JSON.parse(cached);
        this.typeCountCache = new Map(data);
      }
    } catch (error) {
      console.warn('[EformsFieldCounterService] Erreur lors du chargement du cache:', error);
    }
  }

  /**
   * Sauvegarde les compteurs dans le localStorage
   */
  private saveCachedTypeCounts(): void {
    try {
      const data = Array.from(this.typeCountCache.entries());
      localStorage.setItem('eforms-field-count-cache', JSON.stringify(data));
    } catch (error) {
      console.warn('[EformsFieldCounterService] Erreur lors de la sauvegarde du cache:', error);
    }
  }

  /**
   * Obtient le nombre de champs attendu depuis le cache, ou null si pas en cache
   */
  private getExpectedCountFromCache(type: string): number | null {
    const cached = this.typeCountCache.get(type);
    if (cached && Date.now() - cached.timestamp < 24 * 60 * 60 * 1000) { // Cache valide 24h
      return cached.expectedCount;
    }
    return null;
  }

  /**
   * Met en cache le nombre de champs pour un type donné
   */
  private cacheTypeCount(type: string, count: number): void {
    this.typeCountCache.set(type, {
      type,
      expectedCount: count,
      timestamp: Date.now()
    });
    this.saveCachedTypeCounts();
  }

  /**
   * Incrémente le compteur de champs créés pour une sous-section
   */
  incrementCreatedCount(subSectionId: string): void {
    // Ignorer les champs UNK (Unknown/non identifiés)
    const type = this.getTypeFromSubSectionId(subSectionId);
    if (type === 'UNK') {
      return;
    }

    let subSectionCount = this.subSectionCounts.get(subSectionId);

    // Si le compteur n'existe pas, on le crée automatiquement
    if (!subSectionCount) {
      this.initializeSubSectionCount(subSectionId);
      subSectionCount = this.subSectionCounts.get(subSectionId)!;
    }

    const now = Date.now();
    const cachedCount = this.getExpectedCountFromCache(type);

    subSectionCount.createdCount++;
    subSectionCount.lastUpdateTime = now;

    // Vérifier si on a atteint le nombre en cache
    if (cachedCount && subSectionCount.createdCount >= cachedCount) {
      this.markAsComplete(subSectionId, `cache atteint (${cachedCount})`);
    } else {
      // Réinitialiser/programmer le timer de completion
      this.resetCompletionTimer(subSectionId);
    }

    this.subSectionCounts.set(subSectionId, subSectionCount);
    this.subSectionCountsSubject.next(new Map(this.subSectionCounts));
  }

  /**
   * Initialise le compteur pour une sous-section
   */
  initializeSubSectionCount(subSectionId: string): void {
    // Ignorer les champs UNK (Unknown/non identifiés)
    const type = this.getTypeFromSubSectionId(subSectionId);
    if (type === 'UNK') {
      return;
    }

    // Vérifier si le compteur existe déjà pour éviter les doubles initialisations
    if (this.subSectionCounts.has(subSectionId)) {
      return;
    }

    const now = Date.now();


    const subSectionCount: SubSectionFieldCount = {
      subSectionId,
      createdCount: 0,
      isComplete: false,
      lastUpdateTime: now
    };

    this.subSectionCounts.set(subSectionId, subSectionCount);
    this.subSectionCountsSubject.next(new Map(this.subSectionCounts));
  }

  /**
   * Extrait le type depuis l'ID de sous-section
   */
  private getTypeFromSubSectionId(subSectionId: string): string {
    if (subSectionId.includes('_')) {
      return subSectionId.split('_')[0];
    }
    return subSectionId;
  }

  /**
   * Réinitialise le timer de completion pour une sous-section
   */
  private resetCompletionTimer(subSectionId: string): void {
    // Annuler le timer existant
    if (this.completionTimers.has(subSectionId)) {
      clearTimeout(this.completionTimers.get(subSectionId));
    }

    // Programmer un nouveau timer
    const timerId = setTimeout(() => {
      this.onCompletionTimerExpired(subSectionId);
    }, this.COMPLETION_DELAY);

    this.completionTimers.set(subSectionId, timerId);
  }

  /**
   * Appelé quand le timer de completion expire (500ms sans activité)
   */
  private onCompletionTimerExpired(subSectionId: string): void {
    const subSectionCount = this.subSectionCounts.get(subSectionId);
    if (subSectionCount && !subSectionCount.isComplete) {
      const type = this.getTypeFromSubSectionId(subSectionId);

      // Mettre en cache le nombre final pour ce type
      this.cacheTypeCount(type, subSectionCount.createdCount);

      // Marquer comme complet
      this.markAsComplete(subSectionId, `timeout (${subSectionCount.createdCount} champs)`);
    }

    // Nettoyer le timer
    this.completionTimers.delete(subSectionId);
  }

  /**
   * Marque une sous-section comme complète
   */
  private markAsComplete(subSectionId: string, reason: string): void {
    const subSectionCount = this.subSectionCounts.get(subSectionId);
    if (subSectionCount && !subSectionCount.isComplete) {
      const now = Date.now();
      subSectionCount.isComplete = true;
      subSectionCount.completionTime = now;

      this.subSectionCounts.set(subSectionId, subSectionCount);
      this.subSectionCountsSubject.next(new Map(this.subSectionCounts));

      // Nettoyer le timer si il existe
      if (this.completionTimers.has(subSectionId)) {
        clearTimeout(this.completionTimers.get(subSectionId));
        this.completionTimers.delete(subSectionId);
      }
    }
  }

  /**
   * Vérifie si une sous-section est complète
   */
  isSubSectionComplete(subSectionId: string): boolean {
    const subSectionCount = this.subSectionCounts.get(subSectionId);
    return subSectionCount ? subSectionCount.isComplete : false;
  }

  /**
   * Obtient les informations de comptage pour une sous-section
   */
  getSubSectionCount(subSectionId: string): SubSectionFieldCount | undefined {
    return this.subSectionCounts.get(subSectionId);
  }

  /**
   * Observable pour suivre les changements de comptage
   */
  getSubSectionCountsObservable(): Observable<Map<string, SubSectionFieldCount>> {
    return this.subSectionCountsSubject.asObservable();
  }

  /**
   * Remet à zéro le compteur pour une sous-section
   */
  resetSubSectionCount(subSectionId: string): void {
    // Ignorer les champs UNK (Unknown/non identifiés)
    const type = this.getTypeFromSubSectionId(subSectionId);
    if (type === 'UNK') {
      return;
    }

    // Nettoyer le timer existant
    if (this.completionTimers.has(subSectionId)) {
      clearTimeout(this.completionTimers.get(subSectionId));
      this.completionTimers.delete(subSectionId);
    }

    const subSectionCount = this.subSectionCounts.get(subSectionId);
    if (subSectionCount) {
      subSectionCount.createdCount = 0;
      subSectionCount.isComplete = false;
      subSectionCount.lastUpdateTime = Date.now();
      delete subSectionCount.completionTime;

      this.subSectionCounts.set(subSectionId, subSectionCount);
      this.subSectionCountsSubject.next(new Map(this.subSectionCounts));
    }
  }
}
